import { useState, useEffect } from 'react';
import Modal from '../shared/Modal';
import { usePassLevels } from '../../hooks/usePassLevels';
import PassLevelMultiSelect from '../shared/PassLevelMultiSelect';

// Add a new person, or edit an existing one when a `person` prop is supplied.
// In edit mode the existing record is merged so fields we don't expose here
// (Brushfire ID, status, notes, manual-pass flag) are preserved.
export default function AddPersonModal({ open, onClose, onSave, showToast, person = null }) {
  const passLevels = usePassLevels();
  const isEdit = !!(person && person.id);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [passLevel, setPassLevel] = useState('');
  const [mealsOnly, setMealsOnly] = useState(false);
  const [bgCleared, setBgCleared] = useState(false);

  function reset() {
    setFirstName(''); setLastName(''); setEmail(''); setPhone(''); setPassLevel('SL'); setMealsOnly(false); setBgCleared(false);
  }

  // Prefill from the person being edited (or clear for a fresh add) when opened.
  useEffect(() => {
    if (!open) return;
    if (person && person.id) {
      const parts = (person.name || '').trim().split(' ');
      setFirstName(parts[0] || '');
      setLastName(parts.slice(1).join(' '));
      setEmail(person.email || '');
      setPhone(person.phone || '');
      setPassLevel(person.passLevel || '');
      setMealsOnly(!!person.mealsOnly);
      setBgCleared(/clear/i.test(person.bgCheck || ''));
    } else {
      reset();
    }
  }, [open, person]);

  function handleSave() {
    if (!firstName.trim() && !lastName.trim()) { showToast('Name is required.'); return; }
    if (!email.trim()) { showToast('Email is required.'); return; }
    const base = isEdit ? person : { status: 'active', brushfireId: '', notes: '' };
    const result = {
      ...base,
      name: `${firstName.trim()} ${lastName.trim()}`.trim(),
      email: email.trim(),
      phone: phone.trim(),
      passLevel,
      mealsOnly,
    };
    // Only touch bgCheck when the admin actually flipped the checkbox — a CSV
    // status like "Complete (Clear)" must survive an unrelated edit.
    const wasCleared = /clear/i.test((isEdit && person.bgCheck) || '');
    if (bgCleared && !wasCleared) result.bgCheck = 'Manual (Clear)';
    if (!bgCleared && wasCleared) result.bgCheck = '';
    onClose();
    if (onSave) onSave(result);
  }

  // The multi-select unions any already-assigned values into its options itself.
  const passOptions = passLevels;

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      title={isEdit ? 'Edit Person' : 'Add Person'}
      footer={
        <>
          <button className="btn" onClick={() => { reset(); onClose(); }}>Cancel</button>
          <button className="btn btn-dark" onClick={handleSave}>
            {isEdit ? 'Save Changes' : 'Add Person'}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }} className="form-group">
        <div>
          <label className="form-label">First Name</label>
          <input className="form-input" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First name" />
        </div>
        <div>
          <label className="form-label">Last Name</label>
          <input className="form-input" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last name" />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Email</label>
        <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@example.com" />
      </div>
      <div className="form-group">
        <label className="form-label">Phone</label>
        <input className="form-input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="(615) 000-0000" />
      </div>
      <div className="form-group">
        <label className="form-label">Pass Level / Badges <span style={{ fontWeight: 300, color: 'var(--muted)' }}>(select all that apply)</span></label>
        <PassLevelMultiSelect
          value={passLevel}
          options={passOptions}
          onChange={v => setPassLevel(v)}
        />
      </div>
      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={mealsOnly}
            onChange={e => setMealsOnly(e.target.checked)}
            style={{ accentColor: 'var(--black)', marginTop: 3, flexShrink: 0 }}
          />
          <span>
            <span style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>Meals only (all conference days)</span>
            <span style={{ display: 'block', fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 2 }}>
              For staff spouses/guests. Counts them for every meal each day with no serving role or team.
            </span>
          </span>
        </label>
      </div>
      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={bgCleared}
            onChange={e => setBgCleared(e.target.checked)}
            style={{ accentColor: 'var(--black)', marginTop: 3, flexShrink: 0 }}
          />
          <span>
            <span style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>Background check cleared</span>
            <span style={{ display: 'block', fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 2 }}>
              Usually set automatically by the CSV import — check this to manually mark someone as cleared.
            </span>
          </span>
        </label>
      </div>
    </Modal>
  );
}
